// Deterministic regression harness for the ADP core. Pure and synthetic (no
// fetch, no PHI) — every case is inline. Open the wedge with ?adptest to run.
// The billing math reaches a real invoice eventually, so these lock the exact
// cents, not an approximation.

import { computeFunding } from "./funding";
import { fundingPeriodClock, addYears } from "./eligibility";
import { buildAdpClaim } from "./form";
import type { AdpClaimInput, DeviceAid } from "./types";

export interface AdpReport { name: string; pass: boolean; diffs: string[] }

const aid = (costCents: number, serial = "SER123", ear?: "left" | "right"): DeviceAid =>
  ({ make: "Phonak", model: "Audeo Lumity L90", serial, costCents, ear });

const check = (name: string, fn: (diff: (m: string) => void) => void): AdpReport => {
  const diffs: string[] = [];
  try { fn(m => diffs.push(m)); } catch (e) { diffs.push(String(e)); }
  return { name, pass: diffs.length === 0, diffs };
};

const eq = (diff: (m: string) => void, label: string, got: unknown, want: unknown) => {
  if (got !== want) diff(`${label}: got ${JSON.stringify(got)}, want ${JSON.stringify(want)}`);
};

const fullInput = (over: Partial<AdpClaimInput> = {}): AdpClaimInput => ({
  fittingType: "monaural",
  aids: [aid(70000, "AUD2K8X41", "right")],
  applicant: { name: "Jane Doe", healthNumber: "1234567890", dateOfBirth: "1950-05-01", address: "1 King St, North Bay ON" },
  authorizer: { name: "Sean C.", registrationNumber: "AUD-0001", category: "Audiologist" },
  vendor: { name: "Clearwater Hearing", adpVendorNumber: "V-99001", invoiceNumber: "INV-4471", invoiceDate: "2026-07-08", deliveryDate: "2026-07-08" },
  priorFundingDate: null,
  ...over,
});

export function runAdpSelftest(): AdpReport[] {
  return [
    check("monaural $700 caps at $500", d => {
      const f = computeFunding("monaural", [aid(70000)]);
      eq(d, "contribution", f.contributionCents, 50000);
      eq(d, "clientPays", f.clientPaysCents, 20000);
      eq(d, "cappedOut", f.cappedOut, true);
    }),
    check("monaural $600 pays 75% under cap", d => {
      const f = computeFunding("monaural", [aid(60000)]);
      eq(d, "contribution", f.contributionCents, 45000);
      eq(d, "clientPays", f.clientPaysCents, 15000);
      eq(d, "cappedOut", f.cappedOut, false);
    }),
    check("binaural $1400 caps at $1000", d => {
      const f = computeFunding("binaural", [aid(70000), aid(70000)]);
      eq(d, "contribution", f.contributionCents, 100000);
      eq(d, "clientPays", f.clientPaysCents, 40000);
      eq(d, "cappedOut", f.cappedOut, true);
    }),
    check("binaural asymmetric $700+$300 uses combined-cost cap", d => {
      const f = computeFunding("binaural", [aid(70000), aid(30000)]);
      // combined $1000 -> 75% = $750, under the $1000 binaural cap
      eq(d, "contribution", f.contributionCents, 75000);
      eq(d, "clientPays", f.clientPaysCents, 25000);
      eq(d, "cappedOut", f.cappedOut, false);
    }),
    check("FM $2000 caps at $1350", d => {
      const f = computeFunding("fm", [aid(200000)]);
      eq(d, "contribution", f.contributionCents, 135000);
      eq(d, "clientPays", f.clientPaysCents, 65000);
    }),
    check("rounding: $600.01 -> $450.01 exact cents", d => {
      const f = computeFunding("monaural", [aid(60001)]);
      eq(d, "contribution", f.contributionCents, 45001);
    }),
    check("clock: prior 2023-08-01 blocks in 2026", d => {
      const e = fundingPeriodClock("monaural", "2023-08-01", "2026-07-11");
      eq(d, "eligibleFrom", e.eligibleFrom, "2028-08-01");
      eq(d, "eligible", e.eligible, false);
      if (!(e.daysRemaining > 0)) d(`daysRemaining should be > 0, got ${e.daysRemaining}`);
    }),
    check("clock: prior 2020-01-01 eligible in 2026", d => {
      const e = fundingPeriodClock("monaural", "2020-01-01", "2026-07-11");
      eq(d, "eligible", e.eligible, true);
    }),
    check("clock: no prior claim is eligible now", d => {
      const e = fundingPeriodClock("monaural", null, "2026-07-11");
      eq(d, "eligible", e.eligible, true);
      eq(d, "eligibleFrom", e.eligibleFrom, null);
    }),
    check("clock: leap-day anniversary clamps to Feb 28", d => {
      eq(d, "addYears", addYears("2024-02-29", 5), "2029-02-28");
      const e = fundingPeriodClock("monaural", "2024-02-29", "2029-03-01");
      eq(d, "eligible", e.eligible, true);
    }),
    check("full claim: happy path has no blockers", d => {
      const c = buildAdpClaim(fullInput(), "2026-07-11");
      eq(d, "blockers", c.blockers.length, 0);
      eq(d, "contribution cell", c.application.find(x => x.label === "ADP contribution")?.value, "$500.00");
      eq(d, "client cell", c.application.find(x => x.label === "Client portion (collected by vendor)")?.value, "$200.00");
      eq(d, "formVersion", c.formVersion, "2026-03-02");
      if (c.manualActions.length !== 4) d(`expected 4 manual actions, got ${c.manualActions.length}`);
    }),
    check("full claim: bad health number + missing serial block", d => {
      const c = buildAdpClaim(fullInput({
        aids: [aid(70000, "")],
        applicant: { name: "J", healthNumber: "123", dateOfBirth: "1950-05-01", address: "x" },
      }), "2026-07-11");
      if (!c.blockers.some(b => b.includes("OHIP"))) d("expected a health-number blocker");
      if (!c.blockers.some(b => b.toLowerCase().includes("serial"))) d("expected a missing-serial blocker");
    }),
    check("full claim: within funding period blocks", d => {
      const c = buildAdpClaim(fullInput({ priorFundingDate: "2024-01-01" }), "2026-07-11");
      if (!c.blockers.some(b => b.includes("funding period"))) d("expected a funding-period blocker");
    }),
  ];
}
